import { useEffect, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"

import axios from "axios"
import toast from "react-hot-toast"
import moment from "moment/moment"
import Delete from "pages/product/model/delete"
import Filter from "pages/product/model/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useProductModelRepositories from "repositories/product-model"
import { ArrowNarrowRight } from "components/icons/outline"
import Table from "components/table"
import Import from "pages/product/model/import"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

const SORT_OPTIONS = [
    { value: "name", label: "Nama" },
    { value: "long_name", label: "Nama Panjang" },
    { value: "created_at", label: "Dibuat Pada" },
    { value: "updated_at", label: "Diubah Pada" },
    { value: "series.name", label: "Product Series" },
]

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp, submitSearch] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const name = searchParams.get('name')
    const from = searchParams.get('from')
    const to = searchParams.get('to')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')
    const effectiveOrderBy = orderBy || 'name'
    const effectiveDirection = direction || 'asc'

    const { data: productModelData, isLoading: isLoadingProductModelData, mutate: mutateProductModelData } = useProductModelRepositories({
        page,
        search,
        name,
        from,
        to,

        order_by: effectiveOrderBy,
        direction: effectiveDirection
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingProductModelData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [productModelData, isLoadingProductModelData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // 210726 kurasi storefront: toggle tampil/blokir produk di storefront (blok total —
    // hilang dari list, search, promo, detail & similar storefront; POS/CMS tetap).
    const toggleYoiboiVisibility = async (row) => {
        const next = Number(row.show_in_yoiboi ?? 1) === 1 ? 0 : 1
        try {
            await axios.post(`${process.env.REACT_APP_INVENTORY_API_URL}/product/model/${row.id}/yoiboi-visibility`, { show_in_yoiboi: next })
            toast.success(next === 1 ? "Produk ditampilkan di storefront" : "Produk diblokir dari storefront")
            mutateProductModelData()
        } catch (e) {
            toast.error(e?.response?.data?.message || "Gagal mengubah visibilitas storefront")
        }
    }

    const mergeParams = (nextValues) => {
        const nextParams = new URLSearchParams(searchParams)

        Object.entries(nextValues).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                nextParams.delete(key)
            } else {
                nextParams.set(key, value)
            }
        })

        setSearchParams(nextParams)
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    // Data list props
    const dataListHeaders = [
        ...configuration('hierarchy').find((row) => row.key === 'product-series').value ? [{
            isAction: false,
            content: "Product Series",
            sortKey: "series.name"
        }] : [],
        {
            isAction: false,
            content: "Nama",
            sortKey: "name"
        },
        {
            isAction: false,
            content: "Nama Panjang",
            sortKey: "long_name"
        },
        // {
        //     isAction: false,
        //     content: "Storefront"
        // },
        {
            isAction: false,
            content: lang.created_at,
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: lang.updated_at,
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        ...configuration('hierarchy').find((row) => row.key === 'product-series').value ? [{
            type: "default",
            bold: false,
            content: (value) => (
                <div className="flex flex-col space-y-1">
                    <div className="flex space-x-1">
                        <span>{value.product_series?.product_brand?.product_type?.name}</span>
                        <span>
                            <ArrowNarrowRight className="w-4 h-4" />
                        </span>
                        <span>{value.product_series?.product_brand?.brand?.name}</span>
                        {/* <span className="font-medium underline text-neutral-700 decoration-skip-ink decoration-4 decoration-neutral-500/20 underline-offset-[-2px]">Link</span> */}
                    </div>
                    <span className="font-medium text-neutral-900">{value.product_series?.series?.name}</span>
                </div>
            )
        }] : [],
        {
            type: "default",
            bold: true,
            content: (value) => value.name
        },
        {
            type: "default",
            bold: true,
            content: (value) => value.long_name
        },
        // {
        //     type: "default",
        //     bold: false,
        //     content: (value) => {
        //         const visible = Number(value.show_in_yoiboi ?? 1) === 1
        //         return (
        //             <button
        //                 type="button"
        //                 disabled={!can('product-model.update')}
        //                 onClick={() => toggleYoiboiVisibility(value)}
        //                 title={visible ? "Klik untuk blokir dari storefront" : "Klik untuk tampilkan di storefront"}
        //                 className={`px-2 py-1 text-[10px] font-semibold rounded-lg transition ${visible ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-red-100 text-red-700 hover:bg-red-200"} ${can('product-model.update') ? "cursor-pointer" : "cursor-default"}`}
        //             >
        //                 {visible ? "Tampil" : "Diblokir"}
        //             </button>
        //         )
        //     }
        // },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('product-model.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-brand-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('product-model.delete') && (
                        <Delete data={value} onSuccess={() => mutateProductModelData()} />
                    )}
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }} value={searchTemp} type="text" placeholder={`${lang.search} Product Model`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <div className="hidden sm:block">
                        <select
                            value={effectiveOrderBy}
                            onChange={(e) => mergeParams({ order_by: e.target.value, direction: effectiveDirection })}
                            className="py-3 pl-3 pr-8 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            {SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="hidden sm:block">
                        <select
                            value={effectiveDirection}
                            onChange={(e) => mergeParams({ order_by: effectiveOrderBy, direction: e.target.value })}
                            className="py-3 pl-3 pr-8 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                        >
                            <option value="asc">Asc</option>
                            <option value="desc">Desc</option>
                        </select>
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, from, to }).filter(([_, v]) => v != null))} />
                    <Import onSuccess={() => mutateProductModelData()} />
                </div>
                <div className="flex items-center space-x-2">
                    {can('product-model.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-brand-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <Table isLoading={isLoadingProductModelData} data={productModelData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={productModelData?.data.length === 0 && !search && !isLoadingProductModelData} isEmptyOnSearching={productModelData?.data.length === 0 && search && !isLoadingProductModelData} toggleSort={toggleSort} orderBy={effectiveOrderBy} direction={effectiveDirection} />
            <Pagination links={productModelData?.links} from={productModelData?.from} to={productModelData?.to} total={productModelData?.total} />
        </div>
    )
}
